import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd, breadcrumbJsonLd } from "@/lib/seo-jsonld";
import {
  SOURCE_ALKOHOLLAGEN,
  SOURCE_FOHM_VAGLEDNING,
  SOURCE_FOHM_KUNSKAPSPROV,
} from "@/lib/official-sources";

const URL = "https://alkoholprovet.se/serveringsansvarig-anmalan";

const faqs = [
  {
    q: "Vad är en serveringsansvarig person?",
    a: "Det är den person som tillståndshavaren utser att ha tillsyn över alkoholserveringen när tillståndshavaren själv inte är på plats. Rollen framgår av 8 kap. 18 § alkohollagen (2010:1622).",
  },
  {
    q: "Hur gammal måste en serveringsansvarig vara?",
    a: "Minst 20 år. Personen ska dessutom vara lämplig för uppgiften med hänsyn till sina personliga egenskaper och omständigheterna i verksamheten.",
  },
  {
    q: "Måste serveringsansvariga anmälas till kommunen?",
    a: "Ja. Tillståndshavaren ska anmäla vilka personer som är utsedda till serveringsansvariga till kommunen, och hålla förteckningen aktuell när personer tillkommer eller slutar.",
  },
  {
    q: "Måste någon serveringsansvarig vara närvarande hela serveringstiden?",
    a: "Ja. Tillståndshavaren eller en av denne utsedd serveringsansvarig person ska finnas på serveringsstället under hela serveringstiden.",
  },
  {
    q: "Måste serveringsansvarig ha gjort kunskapsprovet?",
    a: "Nej. Kunskapsprovet krävs av tillståndshavaren och personer med betydande inflytande, inte av varje serveringsansvarig. Kommunen förutsätter däremot att den serveringsansvarige kan reglerna om ålder, nykterhet, ordning och serveringstider.",
  },
  {
    q: "Vilken skillnad är det på serveringsansvarig och vanlig serveringspersonal?",
    a: "Serveringspersonal serverar och nekar servering i det enskilda fallet. Serveringsansvarig har tillsyn över hela serveringen under passet, är anmäld till kommunen, minst 20 år och den som personalen vänder sig till vid problem.",
  },
  {
    q: "Vem straffas om en anställd serverar en underårig?",
    a: "Den anställde kan ha ett eget straffrättsligt ansvar, men det tillståndsrättsliga ansvaret ligger alltid hos tillståndshavaren, som riskerar erinran, varning eller återkallelse av tillståndet.",
  },
  {
    q: "Hur många serveringsansvariga bör vi anmäla?",
    a: "Så många att det alltid finns en anmäld person i tjänst under hela serveringstiden, även vid sjukdom och semester. Många verksamheter anmäler samtliga skiftansvariga.",
  },
];

const sections = [
  {
    h2: "Vad kravet innebär",
    body: "Enligt 8 kap. 18 § alkohollagen (2010:1622) ska tillståndshavaren eller en av denne utsedd serveringsansvarig person ha tillsyn över serveringen och finnas på serveringsstället under hela serveringstiden. Kravet gäller oavsett om ni har stadigvarande tillstånd till allmänheten, tillstånd till slutet sällskap eller ett tillfälligt tillstånd.\n\nFör den som utses gäller två grundkrav:\n\n• Personen ska ha fyllt 20 år.\n• Personen ska vara lämplig för uppgiften med hänsyn till sina personliga egenskaper och omständigheterna i verksamheten.\n\nDärutöver ska tillståndshavaren anmäla de utsedda personerna till kommunen. Kravet ligger kvar oförändrat efter lagändringen den 1 juni 2026 – det var mat-, köks- och sittplatskraven som slopades, inte reglerna om serveringsansvar och tillsyn.",
  },
  {
    h2: "Serveringsansvarig, serveringspersonal och tillståndshavare",
    body: "De tre rollerna blandas ofta ihop, både i verksamheten och på kunskapsprovet.\n\nTillståndshavaren är den fysiska eller juridiska person som tillståndet är utfärdat för. Ansvaret för att verksamheten följer alkohollagen kan inte avtalas bort eller delegeras. Sanktioner riktas alltid mot tillståndshavaren, även när felet begicks av en anställd.\n\nServeringsansvarig är den person tillståndshavaren utser att ha tillsyn över serveringen under ett pass. Personen ska vara minst 20 år, lämplig för uppgiften, anmäld till kommunen och närvarande på serveringsstället. Den serveringsansvarige leder serveringen på plats: bedömer nykterhet, stoppar servering, hanterar ordningsfrågor och är den som personal och tillsynsbesök vänder sig till.\n\nServeringspersonal är övriga anställda som serverar. Den som serverar alkoholdrycker ska ha fyllt 20 år, med begränsade undantag för folköl och för lärlingar under utbildning. Personalen ansvarar för sina egna serveringsbeslut i det enskilda fallet, men behöver inte anmälas till kommunen och behöver inte vara utsedd till serveringsansvarig.\n\nEn person kan bära flera roller samtidigt: en ägare kan vara både tillståndshavare, serveringsansvarig och den som serverar.",
  },
  {
    h2: "Närvarokravet i praktiken",
    body: "Närvaro betyder att personen faktiskt finns på serveringsstället och kan utöva tillsyn – inte att hen är nåbar på telefon. Praktiska konsekvenser:\n\n• Schemalägg så att en anmäld serveringsansvarig alltid är i tjänst från serveringens början till dess slut.\n• Vid flera våningar, uteservering eller flera avdelningar behöver ni tillräckligt många ansvariga för att hela serveringsytan ska kunna överblickas.\n• Vid skiftbyte ska det vara tydligt vem som lämnar över och vem som tar över ansvaret.\n• Vid tillfälliga tillstånd och evenemang gäller kravet under hela evenemangets serveringstid.\n• Ordningsvakter ersätter inte en serveringsansvarig – de har en annan uppgift och annan lagstiftning bakom sig.\n\nVid tillsynsbesök är en av de första frågorna kommunen ställer just vem som är serveringsansvarig på plats. Kan ingen svara, eller är personen inte anmäld, är det en brist i sig.",
  },
  {
    h2: "Så anmäler ni serveringsansvariga till kommunen",
    body: "Anmälan görs till kommunens tillståndsenhet där serveringsstället ligger. Rutinen skiljer sig något mellan kommuner, men innehållet är i praktiken detsamma:\n\n1. Sammanställ namn och personnummer för varje person som ska vara serveringsansvarig.\n2. Kontrollera att var och en har fyllt 20 år.\n3. Fyll i kommunens blankett eller e-tjänst \"Anmälan av serveringsansvarig personal\" – ofta som en förteckning där hela gruppen anmäls i samma dokument.\n4. Skicka in anmälan, undertecknad av tillståndshavaren eller behörig firmatecknare.\n5. Uppdatera anmälan när någon slutar eller tillkommer. Skicka in en ny, komplett förteckning i stället för lösa tillägg – då är det tydligt vad som gäller.\n6. Spara en kopia i egenkontrollprogrammet tillsammans med kvittot på att anmälan skickats.\n\nAnmälan är avgiftsfri i de flesta kommuner och kräver normalt inget beslut – kommunen registrerar förteckningen. Att anmäla ny personal är också en av de ändringar som ska anmälas löpande under tillståndets livstid.",
  },
  {
    h2: "Ansvar under passet",
    body: "Den serveringsansvarige ska se till att serveringen sker enligt alkohollagen. De punkter som väger tyngst i tillsynen:\n\n• Ingen servering till den som är märkbart påverkad av alkohol eller annat berusningsmedel (8 kap. 20 §).\n• Ingen servering till någon under 18 år, och ålderskontroll vid tvivel.\n• Ordning och nykterhet på serveringsstället, inklusive uteservering.\n• Att serveringstiderna hålls och att lokalen töms enligt beslutet.\n• Att personalen känner till rutinerna i egenkontrollprogrammet och vet när servering ska nekas.\n• Att alkoholdrycker inte tas med ut från eller in i serveringsytan i strid med tillståndet.\n\nDokumentera avvikelser – nekad servering, avvisad gäst, incident i entrén. En kort loggpost är det bästa underlaget om kommunen eller polisen senare ställer frågor.",
  },
  {
    h2: "Vanliga misstag",
    body: "• Bara ägaren är anmäld, men ägaren är inte på plats varje kväll. Då står serveringen utan ansvarig person.\n• Förteckningen hos kommunen är gammal och innehåller personal som slutat.\n• En 18- eller 19-årig skiftledare utses – åldersgränsen för serveringsansvarig är 20 år.\n• Anmälan glöms bort vid ägarbyte eller när verksamheten flyttas till ett nytt bolag; nytt tillstånd innebär ny anmälan.\n• Serveringsansvarig är utsedd men har inte fått genomgå rutinerna för ålderskontroll och nekad servering.\n• Uteserveringen bemannas utan att någon ansvarig kan överblicka den.",
  },
];

function Page() {
  return (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Serveringsansvarig – anmälan" },
      ]}
      title="Serveringsansvarig – krav, ansvar och anmälan till kommunen"
      intro="Vad en serveringsansvarig person är, vilka krav som gäller (20 år, lämplighet, närvaro under hela serveringstiden), hur anmälan till kommunen görs och skillnaden mot serveringspersonal och tillståndshavare."
      sections={sections}
      faqs={faqs}
      lastReviewed="2026-09-14"
      related={{
        title: "Öva och gå vidare",
        links: [
          { to: "/provfragor/serveringsansvar", label: "Provfrågor om serveringsansvar" },
          { to: "/ansokan-serveringstillstand-checklista", label: "Checklista för ansökan om serveringstillstånd" },
          { to: "/egenkontrollprogram-mall", label: "Egenkontrollprogram – mall" },
          { to: "/alderskontroll-servering", label: "Ålderskontroll vid servering" },
          { to: "/alkohollagen-kapitel-8", label: "Alkohollagen kapitel 8 (servering)" },
          { to: "/provfragor/alderskontroll", label: "Provfrågor om ålderskontroll" },
          { to: "/practice", label: "Träna på provfrågor" },
        ],
      }}
      sources={[
        SOURCE_ALKOHOLLAGEN,
        SOURCE_FOHM_VAGLEDNING,
        SOURCE_FOHM_KUNSKAPSPROV,
      ]}
    />
  );
}

export const Route = createFileRoute("/serveringsansvarig-anmalan")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Serveringsansvarig – krav, ansvar och anmälan 2026" },
      {
        name: "description",
        content:
          "Serveringsansvarig person enligt 8 kap. 18 § alkohollagen: 20-årskrav, lämplighet, närvaro under hela serveringstiden, anmälan till kommunen och skillnaden mot serveringspersonal.",
      },
      { name: "keywords", content: "serveringsansvarig, anmälan serveringsansvarig, serveringsansvarig person, 8 kap 18 § alkohollagen" },
      { property: "og:title", content: "Serveringsansvarig – krav, ansvar och anmälan" },
      {
        property: "og:description",
        content: "Krav, närvaro, ansvar under passet och så anmäler ni serveringsansvariga till kommunen.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Serveringsansvarig – anmälan", url: URL },
      ]),
      articleJsonLd({
        url: URL,
        headline: "Serveringsansvarig – krav, ansvar och anmälan till kommunen",
        description:
          "Krav på serveringsansvarig person enligt 8 kap. 18 § alkohollagen, närvarokravet, anmälan till kommunen och rollernas skillnader.",
        datePublished: "2026-09-14",
        dateModified: "2026-09-14",
      }),
    ],
  }),
});
